import { useState, useCallback, RefObject } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';

export interface ExportPdfHeader {
  candidateName: string;
  candidateEmail: string;
  date: string;
  jobTitle?: string;
}

export interface ExportPdfOptions {
  /** Reference to the DOM element to capture */
  contentRef: RefObject<HTMLElement>;
  /** PDF filename (including .pdf extension) */
  fileName: string;
  /** Header data for the PDF */
  header: ExportPdfHeader;
}

export interface UseExportPdfReturn {
  exportPdf: () => Promise<void>;
  isExporting: boolean;
}

// A4 dimensions in mm
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const MARGIN = 15;
const CONTENT_WIDTH = A4_WIDTH - MARGIN * 2;
const HEADER_Y = MARGIN;
const FOOTER_HEIGHT = 10;

/**
 * Adds a header section to the PDF with candidate info.
 */
function addHeader(doc: jsPDF, header: ExportPdfHeader): number {
  let y = HEADER_Y;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(header.candidateName, MARGIN, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(header.candidateEmail, MARGIN, y);
  y += 5;

  doc.text(`Data: ${header.date}`, MARGIN, y);
  y += 5;

  if (header.jobTitle) {
    doc.text(`Vaga: ${header.jobTitle}`, MARGIN, y);
    y += 5;
  }

  // Separator line
  y += 2;
  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, y, A4_WIDTH - MARGIN, y);
  y += 5;

  return y;
}

/**
 * Adds a footer with "Gerado por TalentumIQ" and current date/time in pt-BR format.
 */
function addFooter(doc: jsPDF): void {
  const now = new Date();
  const formatted = now.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const footerY = A4_HEIGHT - FOOTER_HEIGHT;

  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, footerY - 3, A4_WIDTH - MARGIN, footerY - 3);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Gerado por TalentumIQ', MARGIN, footerY);
  doc.text(formatted, A4_WIDTH - MARGIN, footerY, { align: 'right' });
}

export function useExportPdf(options: ExportPdfOptions): UseExportPdfReturn {
  const { contentRef, fileName, header } = options;
  const [isExporting, setIsExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    if (!contentRef.current) return;

    try {
      setIsExporting(true);

      // Capture the DOM element as a canvas
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Add header and get the Y position where content starts
      const contentStartY = addHeader(doc, header);

      // Available height for the captured content (between header and footer)
      const availableHeight = A4_HEIGHT - contentStartY - FOOTER_HEIGHT - 5;

      // Convert canvas to image and calculate dimensions to fit within content area
      const imgData = canvas.toDataURL('image/png');
      const imgAspectRatio = canvas.width / canvas.height;
      let imgWidth = CONTENT_WIDTH;
      let imgHeight = imgWidth / imgAspectRatio;

      // Scale down if the image exceeds available height
      if (imgHeight > availableHeight) {
        imgHeight = availableHeight;
        imgWidth = imgHeight * imgAspectRatio;
      }

      doc.addImage(imgData, 'PNG', MARGIN, contentStartY, imgWidth, imgHeight);

      // Add footer
      addFooter(doc);

      // Save the PDF
      doc.save(fileName);
    } catch {
      toast.error('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  }, [contentRef, fileName, header]);

  return { exportPdf, isExporting };
}
